/**
 * Fiche d'un compte (admin) : les Moves que ce compte a débloqués dans sa trousse (migration 0750), avec
 * la date, et de quoi les retirer — un par un ou tous. Retiré, un Move redevient à prendre : ses touches
 * disparaissent et la trousse le reproposera quand une question en aura besoin.
 */
import { Shapes, Trash2 } from 'lucide-react';
import { moveById } from '@/features/moves/move-catalog';
import { useDeleteUserMoves, useUserMoves } from '@/lib/moves/user-moves';

const DATE = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

export function UserMovesSection({ userId, name }: { userId: string; name?: string | null }) {
  const q = useUserMoves(userId);
  const del = useDeleteUserMoves(userId);
  const rows = q.data ?? [];
  const who = name?.split(/\s+/)[0] || 'ce compte';

  const removeOne = (moveId: string, label: string) => {
    if (!window.confirm(`Retirer le Move « ${label} » à ${who} ?`)) return;
    del.mutate([moveId]);
  };
  const removeAll = () => {
    if (!window.confirm(`Retirer les ${rows.length} Moves de ${who} ? Il devra les reprendre.`))
      return;
    del.mutate(undefined);
  };

  return (
    <section
      className="border-ink-200 space-y-3 rounded-2xl border p-4"
      aria-labelledby={`moves-${userId}`}
    >
      <h2
        id={`moves-${userId}`}
        className="font-display text-ink-900 flex items-center gap-2 text-base font-bold"
      >
        <Shapes className="h-5 w-5" aria-hidden /> Moves débloqués
        {rows.length > 0 && (
          <span className="text-ink-500 text-sm font-normal">({rows.length})</span>
        )}
      </h2>
      {q.isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : !rows.length ? (
        <p className="text-ink-500 text-sm">Aucun Move débloqué pour l’instant.</p>
      ) : (
        <>
          <ul className="divide-ink-100 divide-y" data-testid="user-moves">
            {rows.map((r) => {
              const m = moveById(r.move_id);
              const label = m?.name ?? r.move_id;
              return (
                <li key={r.move_id} className="flex items-center gap-3 py-1.5">
                  <span
                    aria-hidden
                    className={
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-base font-bold ' +
                      (m?.kind === 'geste'
                        ? 'bg-[#efe9fc] text-[#5b32b0]'
                        : 'bg-[#fdf0c8] text-[#8a6508]')
                    }
                  >
                    {m?.kind === 'geste' ? '↹' : '✦'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-ink-900 block text-sm font-semibold">{label}</span>
                    <span className="text-ink-500 block text-xs">
                      {m
                        ? m.kind === 'geste'
                          ? 'Geste'
                          : 'Connaissance'
                        : 'Move inconnu du catalogue'}{' '}
                      · {DATE.format(new Date(r.unlocked_at))}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={del.isPending}
                    onClick={() => removeOne(r.move_id, label)}
                    aria-label={`Retirer le Move ${label}`}
                    className="text-ink-500 hover:text-error-600 flex min-h-11 min-w-11 items-center justify-center rounded-xl disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            disabled={del.isPending}
            onClick={removeAll}
            className="text-error-600 min-h-11 text-sm font-semibold underline underline-offset-2 disabled:opacity-50"
          >
            Retirer tous les Moves
          </button>
        </>
      )}
      {(q.error || del.error) && (
        <p role="alert" className="text-error-600 text-sm">
          {(del.error ?? q.error)?.message}
        </p>
      )}
    </section>
  );
}
